import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export function getAuth() {
  try {
    return JSON.parse(localStorage.getItem("mc_auth")) || null;
  } catch {
    return null;
  }
}

export function setAuth(auth) {
  if (auth) localStorage.setItem("mc_auth", JSON.stringify(auth));
  else localStorage.removeItem("mc_auth");
}

api.interceptors.request.use((config) => {
  const auth = getAuth();
  if (auth?.token) config.headers.Authorization = `Bearer ${auth.token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setAuth(null);
      if (!window.location.pathname.match(/^\/(login|citizen|$)/)) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return e?.message || "Something went wrong";
}

export const fmtINR = (n) =>
  "\u20B9" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export const fmtDate = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

// A plain "YYYY-MM-DD" day (filter values, business dates) — never shifted by the device timezone.
export const fmtDay = (s) => {
  if (!s) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (!m) return fmtDate(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Clock time in IST regardless of the viewer's device timezone (Phase 88: sign-in times)
export const fmtTimeIST = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch {
    return s;
  }
};

// Calendar date in IST (Phase 145: batch history dates must match the IST clock time shown beside them)
export const fmtDateIST = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch {
    return s;
  }
};

// Full date + time in IST (Phase 90: audit trail "When")
export const fmtDateTime = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kolkata",
    });
  } catch {
    return s;
  }
};

// Phase 184: a stored IST clock string ("HH:MM" or "HH:MM:SS") -> 12-hour "10:35 AM"
export const fmtTime12 = (t) => {
  if (!t) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
  if (!m) return String(t);
  const h = Number(m[1]);
  if (Number.isNaN(h) || h > 23) return String(t);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
};

// Phase 184: business date (YYYY-MM-DD) + IST clock -> "06 Oct 2026, 10:35 AM" (date only when no clock is known)
export const fmtDateAtTime = (date, time) => {
  const d = fmtDay(date);
  const t = fmtTime12(time);
  return t ? `${d}, ${t}` : d;
};


// Today's calendar date in IST as YYYY-MM-DD — the app's business day, regardless of device timezone
// (toISOString() would give the UTC date, which is "yesterday" between 00:00 and 05:30 IST)
export const istDateISO = (d = new Date()) => {
  try {
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return d.toISOString().slice(0, 10);
  }
};

// Absolute URL for backend-served files (url like "/api/files/photos/x.jpg")
export const fileHref = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

// Upload a browser File via the base64 file endpoint. Returns {url, filename}.
// Phase 130: images are compressed to a <= 200 KB JPEG on the device first; PDFs/documents go up untouched.
export async function uploadFileB64(code, file) {
  const { compressImage } = await import("./imageCompress");
  const res = await compressImage(file);
  const upload = res.file || file;
  if (upload.size > 8 * 1024 * 1024) throw new Error(`${file.name} is larger than 8MB`);
  const data = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(upload);
  });
  const name = res.compressed ? res.filename : file.name;
  const resp = await api.post(`/t/${code}/files/photo`, { data, filename: name });
  return { url: resp.data.url, filename: name, size: resp.data.size, compressed: !!(res.compressed || resp.data.compressed) };
}

// Authenticated blob download (PDF certificates, XLSX templates, ...)
// `opts.data` switches to a POST with that JSON body (Phase 216: export of an explicit selection).
export async function downloadBlob(path, filename, opts = {}) {
  let r;
  try {
    r = opts.data !== undefined
      ? await api.post(path, opts.data, { responseType: "blob" })
      : await api.get(path, { responseType: "blob" });
  } catch (e) {
    // Error bodies arrive as a Blob when responseType is "blob" — decode so errMsg() can read `detail`.
    const b = e?.response?.data;
    if (b instanceof Blob) {
      try { e.response.data = JSON.parse(await b.text()); } catch { /* keep the generic message */ }
    }
    if (!e?.response && e?.message === "Network Error") e.message = "The download took too long or the connection dropped. Please try again.";
    throw e;
  }
  // Prefer the server's filename (Content-Disposition) — it carries the employee code / filter / date.
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : filename;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Authenticated file VIEW: fetches with the auth token and opens in a new tab
// (browser renders images/PDFs inline; other types download with their filename).
export async function viewAuthedFile(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const type = r.data.type || "application/octet-stream";
  const viewable = /^image\/|^application\/pdf|^text\//.test(type);
  const url = URL.createObjectURL(r.data);
  if (viewable) {
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
